import { useCallback, useState } from "react";

import { type CloudFlowNodeType, NODE_STATUS, type NodeConfigApiParameters } from "@doitintl/cmp-models";
import { Avatar, Button, Card, Stack, Typography } from "@mui/material";

import { cloudflowTexts } from "../../../../../assets/texts";
import { Loader } from "../../../../../Components/Loader";
import { ActionInputForm } from "../../ApiActionParametersForm/ActionInputForm";
import { ReferencedFieldContextProvider } from "../../ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldContextProvider";
import { useModalManager } from "../../Common/CloudflowModalsProvider";
import { useCloudflowOperations } from "../../Common/CloudflowOperationsProvider";
import DisablePublishedFlowGuard from "../../Common/DisablePublishedFlowGuard";
import { useReferenceableNodes } from "../../Common/hooks/useReferenceableNodes";
import { useApiProviderLogo } from "../../Common/utils";
import { useNodeConfigurationContext } from "../NodeConfigurationContext";
import Approval from "./Approval/Approval";
import { useErrorUpdates } from "./hooks";
import { NodeCardHeader } from "./NodeCardHeader";

type ApiActionInput = Pick<NodeConfigApiParameters, "configurationValues" | "formValues">;

const APIParametersTab = () => {
  const { nodeConfig, updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const { openModal } = useModalManager();
  const { httpOperationLoading } = useCloudflowOperations();

  const providerLogo = useApiProviderLogo();
  const [referenceableNodes, referenceableNodesLoading] = useReferenceableNodes(nodeConfig.id);

  const [isValid, setIsValid] = useState<boolean>(nodeConfig.status === NODE_STATUS.VALIDATED);

  const onInputChange = useCallback(
    (input: object) => {
      updateNode((prevNode) => ({ parameters: { ...prevNode.parameters!, ...input } }));
    },
    [updateNode]
  );

  useErrorUpdates<CloudFlowNodeType.ACTION>({
    isValid,
    errorKey: "param_error",
    errorMessage: cloudflowTexts.STATUS.INCORRECT_FORM_PARAMETERS_ERROR,
    isFormLoading: referenceableNodesLoading,
  });

  return (
    <DisablePublishedFlowGuard>
      <Stack
        sx={{
          p: 2,
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          Service and action
        </Typography>
        <Card>
          <NodeCardHeader
            avatar={<Avatar src={providerLogo(nodeConfig.parameters.provider)} />}
            subheader={nodeConfig.parameters.operation.service}
            title={nodeConfig.parameters.operation.id}
            action={
              <Button
                disabled={httpOperationLoading}
                onClick={() => {
                  openModal("action");
                }}
              >
                Change
              </Button>
            }
          />
        </Card>
        <Approval />
        <Loader loading={referenceableNodesLoading}>
          <ReferencedFieldContextProvider
            referenceableNodes={referenceableNodes}
            values={nodeConfig.parameters.formValues}
          >
            <ActionInputForm<ApiActionInput>
              input={nodeConfig.parameters}
              operationPointer={nodeConfig.parameters.operation}
              onInputChange={onInputChange}
              onInputValidityChange={setIsValid}
            />
          </ReferencedFieldContextProvider>
        </Loader>
      </Stack>
    </DisablePublishedFlowGuard>
  );
};

export default APIParametersTab;
